import { groupBy, keyBy, mapValues, memoize, sortBy } from 'lodash'
import { zeroAddress, type Chain } from 'viem'
import {
  mainnet,
  optimism,
  arbitrum,
  base,
  bsc,
  celo,
  hyperEvm,
  polygon,
  tempo,
  worldchain,
  megaeth,
  arc,
} from 'viem/chains'

import {
  ADDRESS_ZERO,
  BSC_USDT_ADDRESS,
  HYPEREVM_USDT0_ADDRESS,
  MEGAETH_USDM_ADDRESS,
  MEGAETH_USDT0_ADDRESS,
  POLYGON_USDT_ADDRESS,
  TEMPO_BRIDGED_USDC_ADDRESS,
  TEMPO_USD_ADDRESS,
  USDC_TOKENS,
} from '~/constants/addresses'
import { VmType } from '~/types'

export const SupportedVmTypes = ['evm', 'svm'] as const satisfies VmType[]

/** @deprecated Chain ids are plain numbers now; drop the casts as files are touched. */
export type SupportedChainId = number

export type ChainCapability =
  'balances' | 'transact' | 'swap' | 'bridge' | 'accounting' | 'nfts'

export type ChainTier = 'full' | 'partial'

/** Mirrors the `GET /api/metadata/chains` wire shape exactly. */
export type RegistryChain = {
  chainId: number
  name: string
  slug: string
  /** No trailing slash. */
  explorer?: { url: string; name: string }
  /** Alchemy network subdomain, e.g. `eth-mainnet`. */
  alchemyPrefix?: string
  nativeCurrency: {
    name: string
    symbol: string
    decimals: number
    coingeckoId?: string
    logoUri?: string
  }
  supportsEns: boolean
  ensCoinType?: number
  supportsTenderly: boolean
  jiffyscanName?: string
  capabilities: Readonly<Record<ChainCapability, boolean>>
  splitsProtocol?: true
  testnet: boolean
  rolloutFlag?: string
  /** Derived server-side from `capabilities`; the client never recomputes it. */
  tier: ChainTier
}

/**
 * Display order the product has always used, most-used chain first. Chains the
 * server adds later that aren't listed here sort after these, in server order.
 */
const CHAIN_DISPLAY_RANK = [
  1, 8453, 10, 42161, 42220, 480, 4217, 137, 56, 999, 4663,
]

const rankOf = (chainId: number) => {
  const index = CHAIN_DISPLAY_RANK.indexOf(chainId)
  return index === -1 ? CHAIN_DISPLAY_RANK.length : index
}

let registry: RegistryChain[] | undefined

/** Set-once: the first payload wins, later calls are ignored so a refetch can't
 * swap chain metadata out from under a rendered tree. */
export const initChainRegistry = (chains: RegistryChain[]): void => {
  if (registry) return
  registry = sortBy(chains, (chain) => rankOf(chain.chainId))
}

export const isChainRegistryInitialized = (): boolean => registry !== undefined

const requireRegistry = (): RegistryChain[] => {
  if (!registry)
    throw new Error('Chain registry accessed before initialization')
  return registry
}

export const getChain = (chainId: number): RegistryChain | undefined =>
  requireRegistry().find((chain) => chain.chainId === chainId)

export const getChainOrThrow = (chainId: number): RegistryChain => {
  const chain = getChain(chainId)
  if (!chain) throw new Error(`Unknown chain: ${chainId}`)
  return chain
}

/** Every chain the product lists. Testnets live in the registry but are never listed. */
export const getAllChains = (): RegistryChain[] =>
  requireRegistry().filter((chain) => !chain.testnet)

export const getListedChainIds = (): number[] =>
  getAllChains().map((chain) => chain.chainId)

export const hasCapability = (
  chainId: number,
  capability: ChainCapability,
): boolean => getChain(chainId)?.capabilities[capability] ?? false

export const getTier = (chainId: number): ChainTier | undefined =>
  getChain(chainId)?.tier

export const getRpcUrl = (chainId: number): string => {
  const { alchemyPrefix } = getChainOrThrow(chainId)
  if (!alchemyPrefix) throw new Error(`No RPC configured for chain ${chainId}`)
  return `https://${alchemyPrefix}.g.alchemy.com/v2/${process.env.ALCHEMY_API_KEY}`
}

const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11'

export const getViemChain = memoize((chainId: number): Chain => {
  const { name, nativeCurrency, explorer, testnet } = getChainOrThrow(chainId)
  return {
    id: chainId,
    name,
    nativeCurrency: {
      name: nativeCurrency.name,
      symbol: nativeCurrency.symbol,
      decimals: nativeCurrency.decimals,
    },
    rpcUrls: { default: { http: [getRpcUrl(chainId)] } },
    ...(explorer
      ? {
          blockExplorers: {
            default: { name: explorer.name, url: explorer.url },
          },
        }
      : {}),
    contracts: { multicall3: { address: MULTICALL3_ADDRESS } },
    testnet,
  }
})

// A listed chain without an Alchemy endpoint is valid server data (the wire
// contract marks alchemyPrefix optional); it must degrade to not-connectable
// here rather than let getRpcUrl throw during provider mount, where no error
// UI can catch it.
export const getWagmiChains = (): [Chain, ...Chain[]] =>
  getAllChains()
    .filter((chain) => chain.alchemyPrefix)
    .map((chain) => getViemChain(chain.chainId)) as [Chain, ...Chain[]]

// Keep in sync with STABLE_COINS in the backend splits repo.
// Addresses must be checksummed — STABLE_COINS_BY_CHAIN_ID_AND_ADDRESS keys
// by the raw value while isStablecoin checksums the probe.
const STABLE_COINS = [
  // Mainnet
  {
    address: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
    chainId: mainnet.id,
    symbol: 'DAI',
  },
  {
    address: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
    chainId: mainnet.id,
    symbol: 'USDT',
  },
  {
    address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    chainId: mainnet.id,
    symbol: 'USDC',
  },
  // Optimism
  {
    address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
    chainId: optimism.id,
    symbol: 'USDC',
  },
  {
    address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1',
    chainId: optimism.id,
    symbol: 'DAI',
  },
  {
    address: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58',
    chainId: optimism.id,
    symbol: 'USDT',
  },
  // Base
  {
    address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    chainId: base.id,
    symbol: 'USDC',
  },
  {
    address: '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb',
    chainId: base.id,
    symbol: 'DAI',
  },
  {
    address: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2',
    chainId: base.id,
    symbol: 'USDT',
  },
  // Arbitrum
  {
    address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
    chainId: arbitrum.id,
    symbol: 'USDC',
  },
  {
    address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1',
    chainId: arbitrum.id,
    symbol: 'DAI',
  },
  {
    address: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
    chainId: arbitrum.id,
    symbol: 'USDT',
  },
  // Celo
  {
    address: USDC_TOKENS[celo.id],
    chainId: celo.id,
    symbol: 'USDC',
  },
  // Polygon
  {
    address: POLYGON_USDT_ADDRESS,
    chainId: polygon.id,
    symbol: 'USDT',
  },
  {
    address: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063',
    chainId: polygon.id,
    symbol: 'DAI',
  },
  {
    address: USDC_TOKENS[polygon.id],
    chainId: polygon.id,
    symbol: 'USDC',
  },
  {
    address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
    chainId: polygon.id,
    symbol: 'USDC.e',
  },
  // BSC
  {
    address: BSC_USDT_ADDRESS,
    chainId: bsc.id,
    symbol: 'USDT',
  },
  {
    address: USDC_TOKENS[bsc.id],
    chainId: bsc.id,
    symbol: 'USDC',
  },
  // World Chain
  {
    address: USDC_TOKENS[worldchain.id],
    chainId: worldchain.id,
    symbol: 'USDC',
  },
  // HyperEVM
  {
    address: USDC_TOKENS[hyperEvm.id],
    chainId: hyperEvm.id,
    symbol: 'USDC',
  },
  {
    address: HYPEREVM_USDT0_ADDRESS,
    chainId: hyperEvm.id,
    symbol: 'USDT0',
  },
  // Tempo
  {
    address: TEMPO_BRIDGED_USDC_ADDRESS,
    chainId: tempo.id,
    symbol: 'USDC.e',
  },
  {
    address: TEMPO_USD_ADDRESS,
    chainId: tempo.id,
    symbol: 'USD',
  },
  // MegaETH
  {
    address: MEGAETH_USDT0_ADDRESS,
    chainId: megaeth.id,
    symbol: 'USDT0',
  },
  {
    address: MEGAETH_USDM_ADDRESS,
    chainId: megaeth.id,
    symbol: 'USDM',
  },
  // Arc: USDC is the native coin. Its ERC-20 view at 0x3600… reads the same
  // balance, so only the native row is listed.
  {
    address: ADDRESS_ZERO,
    chainId: arc.id,
    symbol: 'USDC',
  },
]

export const STABLE_COINS_BY_CHAIN_ID_AND_ADDRESS: Record<
  string,
  | Record<
      string,
      { address: string; chainId: number; symbol: string } | undefined
    >
  | undefined
> = mapValues(groupBy(STABLE_COINS, 'chainId'), (coins) =>
  keyBy(coins, 'address'),
)

export const getGasToken = (chainId: number) => {
  if (chainId === tempo.id) {
    return TEMPO_USD_ADDRESS
  }
  return zeroAddress
}
